import React from "react";
import { useState, useRef, useEffect } from "react";
import { CheckCircle2, Loader2, Circle, Zap } from "lucide-react";
import { cn } from "@/lib/utils";

interface BuilderBuildingStateProps {
  progress: string;
  logs: string[];
  onAddDetails?: (message: string) => void;
}

const buildSteps = [
  { id: "plan", label: "Understanding your idea" },
  { id: "design", label: "Designing the interface" },
  { id: "build", label: "Writing the code" },
  { id: "finish", label: "Finalizing your app" },
];

function getStepIndex(progress: string): number {
  const p = progress.toLowerCase();
  if (p.includes("final") || p.includes("complet") || p.includes("done") || p.includes("finish")) return 3;
  if (p.includes("writ") || p.includes("build") || p.includes("generat") || p.includes("creat") || p.includes("add")) return 2;
  if (p.includes("design") || p.includes("ui") || p.includes("component") || p.includes("interface") || p.includes("layout")) return 1;
  return 0;
}

export function BuilderBuildingState({ progress, logs }: BuilderBuildingStateProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCurrentStep(getStepIndex(progress));
  }, [progress]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center p-6"
      style={{ background: "#0f0f13", fontFamily: "'DM Sans', system-ui, sans-serif" }}
    >
      <div className="relative w-full max-w-xl">
        {/* Header */}
        <div className="text-center mb-12">
          {/* DayOne Icon */}
          <div className="inline-flex items-center justify-center mb-8">
            <div className="relative">
              <div
                className="w-[52px] h-[52px] flex items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, #a898f8, #cc82e8)",
                  borderRadius: "16px",
                }}
              >
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div
                className="absolute -inset-3 rounded-[20px] animate-ping opacity-30"
                style={{
                  background: "linear-gradient(135deg, #a898f8, #cc82e8)",
                  animationDuration: "2s",
                }}
              />
            </div>
          </div>

          <h2
            className="text-[22px] font-bold mb-2"
            style={{ color: "#ffffff", fontFamily: "'Instrument Serif', serif" }}
          >
            Building your app
          </h2>
          <p className="text-[13px]" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
            This usually takes about 2 minutes
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-12">
          <div className="space-y-0">
            {buildSteps.map((step, index) => {
              const isComplete = index < currentStep;
              const isCurrent = index === currentStep;
              const isPending = index > currentStep;

              return (
                <div
                  key={step.id}
                  className="flex items-center gap-4 py-3 transition-all duration-500"
                >
                  {/* Status Icon */}
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500"
                    style={{
                      background: isComplete
                        ? "rgba(16, 185, 129, 0.2)"
                        : isCurrent
                        ? "rgba(168, 152, 248, 0.2)"
                        : "rgba(255, 255, 255, 0.05)",
                    }}
                  >
                    {isComplete ? (
                      <CheckCircle2 className="w-4 h-4" style={{ color: "rgba(16, 185, 129, 0.9)" }} />
                    ) : isCurrent ? (
                      <div
                        className="w-2.5 h-2.5 rounded-full"
                        style={{
                          background: "#a898f8",
                          animation: "pulse 1.5s ease-in-out infinite",
                        }}
                      />
                    ) : (
                      <Circle className="w-3 h-3" style={{ color: "rgba(255, 255, 255, 0.2)" }} />
                    )}
                  </div>

                  {/* Step Label */}
                  <span
                    className="text-sm transition-colors duration-500"
                    style={{
                      color: isComplete
                        ? "rgba(16, 185, 129, 0.9)"
                        : isCurrent
                        ? "#ffffff"
                        : "rgba(255, 255, 255, 0.25)",
                      fontWeight: isCurrent ? 600 : 400,
                    }}
                  >
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div
              className="h-[3px] rounded-full overflow-hidden"
              style={{ background: "rgba(255, 255, 255, 0.08)" }}
            >
              <div
                className="h-full rounded-full transition-all duration-700 ease-out"
                style={{
                  background: "linear-gradient(90deg, #a898f8, #cc82e8)",
                  width: `${((currentStep + 0.5) / buildSteps.length) * 100}%`,
                }}
              />
            </div>
          </div>
        </div>

        {/* Log Box */}
        <div
          className="rounded-[10px] p-4 max-h-64 overflow-y-auto"
          style={{
            background: "rgba(255, 255, 255, 0.04)",
            border: "1px solid rgba(255, 255, 255, 0.06)",
          }}
        >
          <div className="space-y-2">
            {logs.length === 0 ? (
              <div
                className="flex items-center gap-3 text-[11px] font-mono"
                style={{ color: "rgba(255, 255, 255, 0.6)" }}
              >
                <div
                  className="w-1.5 h-1.5 rounded-full"
                  style={{
                    background: "#a898f8",
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
                Analyzing your idea...
              </div>
            ) : (
              logs.map((log, index) => {
                const isLast = index === logs.length - 1;
                const isDone = index < logs.length - 1;
                return (
                  <div
                    key={index}
                    className="flex items-start gap-3 animate-fade-in text-[11px] font-mono"
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                      style={{
                        background: isDone
                          ? "rgba(16, 185, 129, 0.6)"
                          : isLast
                          ? "#a898f8"
                          : "rgba(255, 255, 255, 0.2)",
                        animation: isLast ? "pulse 1.5s ease-in-out infinite" : "none",
                      }}
                    />
                    <p
                      style={{
                        color: isDone
                          ? "rgba(16, 185, 129, 0.6)"
                          : isLast
                          ? "rgba(255, 255, 255, 0.6)"
                          : "rgba(255, 255, 255, 0.2)",
                      }}
                    >
                      {log}
                    </p>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
}
